import React, { useMemo } from 'react';
import { FileText, Calendar, Trash2, File, ExternalLink } from 'lucide-react';
import { MathJax } from 'better-react-mathjax';

// Composant pour afficher une carte de note dans la liste
export const NoteCard = ({ note, onClick, onDelete }) => {
  return (
    <div 
      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer bg-white hover:border-blue-300"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Titre de la recherche */}
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-green-600 flex-shrink-0" />
            <h4 className="font-semibold text-gray-800 truncate">
              {note.title}
            </h4>
          </div>
          
          {/* Extrait de la synthèse */}
          {note.synthesis?.summary && (
            <div className="mb-3">
              <p className="text-sm text-gray-600 line-clamp-2">
                {note.synthesis.summary}
              </p>
            </div>
          )}
          
          {/* Métadonnées */}
          <div className="flex items-center gap-4 flex-wrap">
            {note.createdAt && (
              <div className="flex items-center text-xs text-gray-500">
                <Calendar className="h-3 w-3 mr-1" />
                {note.createdAt.toDate?.().toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                }) || 'Date inconnue'}
              </div>
            )}
                        
            {note.linkedDocuments && note.linkedDocuments.length > 0 && (
              <div className="flex items-center text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <File className="h-3 w-3 mr-1" />
                {note.linkedDocuments.length} document{note.linkedDocuments.length > 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
        
        {/* Bouton supprimer */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
          title="Supprimer cette note"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Composant pour afficher les détails d'une note
export const NoteDetails = ({ note }) => {
  // Calculer les références de documents (memoized) - UNE RÉFÉRENCE PAR PAGE
  const documentReferences = useMemo(() => {
    if (!note.synthesis?.summary || !note.linkedDocuments) return new Map();
    
    const docRefs = new Map();
    const text = note.synthesis.summary;
    let refCounter = 1;
    
    // Regex pour détecter les références (page X)
    const pageMatches = Array.from(text.matchAll(/\(page (\d+)\)/g));
    
    for (const match of pageMatches) {
      const pageNum = match[1];
      
      // Trouver le document correspondant à cette page
      let docToUse = note.linkedDocuments.find(doc => {
        return doc.excerpt?.includes(`page ${pageNum}`) || 
               doc.content?.includes(`page ${pageNum}`) ||
               doc.pageNumber === parseInt(pageNum);
      });
      
      // Si aucun document spécifique trouvé, prendre le premier avec une URL
      if (!docToUse && note.linkedDocuments.length > 0) {
        docToUse = note.linkedDocuments.find(doc => doc.fileUrl) || note.linkedDocuments[0];
      }
      
      if (docToUse && docToUse.id) {
        // Créer une clé unique par DOCUMENT + PAGE
        const uniqueKey = `${docToUse.id}-page${pageNum}`;
        
        if (!docRefs.has(uniqueKey)) {
          docRefs.set(uniqueKey, {
            num: refCounter++,
            doc: docToUse,
            page: pageNum
          });
        }
      }
    }
    
    return docRefs;
  }, [note.synthesis?.summary, note.linkedDocuments]);

  // Fonction pour mettre en valeur les mots-clés dans le texte
  const highlightKeywords = (text) => {
    if (!text) return text;
    
    // Liste des mots-clés à mettre en valeur
    const keywords = [
      'ChatGPT', 'Aladinn', 'SMART', 'intelligence artificielle', 'IA',
      'gestion de projet', 'workflow', 'automatisation', 'prédictive'
    ];
    
    let highlightedText = text;
    
    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b(${keyword})\\b`, 'gi');
      highlightedText = highlightedText.replace(regex, '<strong class="font-semibold text-gray-900">$1</strong>');
    });
    
    return highlightedText;
  };

  // Fonction pour rendre le texte avec références académiques et phrases cliquables
  const renderTextWithReferences = (text) => {
    if (!text) return null;
    
    // Diviser le texte en paragraphes
    let paragraphs = text.split(/\n\n+/);
    
    if (paragraphs.length === 1 && text.length > 200) {
      const sentences = text.split(/\.\s+(?=[A-Z])/);
      paragraphs = [];
      for (let i = 0; i < sentences.length; i += 3) {
        const chunk = sentences.slice(i, i + 3).join('. ');
        if (chunk) paragraphs.push(chunk + (chunk.endsWith('.') ? '' : '.'));
      }
    }
    
    return paragraphs.map((paragraph, pIndex) => {
      let processedParagraph = highlightKeywords(paragraph);
      
      // Pattern simple : texte suivi de (page X)
      const parts = [];
      const referencePattern = /\(page (\d+)\)/g;
      let lastIndex = 0;
      let match;
      
      // Parcourir toutes les références trouvées
      while ((match = referencePattern.exec(processedParagraph)) !== null) {
        const pageNum = match[1];
        const refPosition = match.index;
        
        // Trouver le début de la phrase (dernier point + espace, ou début du paragraphe)
        let phraseStart = lastIndex;
        const textBeforeRef = processedParagraph.substring(lastIndex, refPosition);
        const lastPeriodIndex = textBeforeRef.lastIndexOf('. ');
        
        if (lastPeriodIndex !== -1) {
          // Il y a du texte avant cette phrase
          parts.push({
            type: 'text',
            content: textBeforeRef.substring(0, lastPeriodIndex + 1)
          });
          phraseStart = lastIndex + lastPeriodIndex + 2; // +2 pour ". "
        }
        
        // Extraire la phrase (du début jusqu'à la référence, sans la référence)
        const phrase = processedParagraph.substring(phraseStart, refPosition).trim();
        
        // Ajouter la phrase cliquable
        parts.push({
          type: 'clickable',
          phrase: phrase,
          pageNum: pageNum
        });
        
        lastIndex = match.index + match[0].length;
      }
      
      // Ajouter le reste du texte après la dernière référence
      if (lastIndex < processedParagraph.length) {
        const remaining = processedParagraph.substring(lastIndex).trim();
        if (remaining) {
          parts.push({
            type: 'text',
            content: remaining
          });
        }
      }
      
      // Si aucune référence trouvée, retourner le texte tel quel
      if (parts.length === 0) {
        return (
          <p key={pIndex} className="mb-5 last:mb-0 text-justify leading-relaxed">
            <span dangerouslySetInnerHTML={{ __html: processedParagraph }} />
          </p>
        );
      }
      
      const renderedParts = parts.map((part, index) => {
        if (part.type === 'clickable') {
          const pageNum = part.pageNum;
          
          // Trouver le document correspondant
          let docToUse = note.linkedDocuments?.find(doc => {
            return doc.excerpt?.includes(`page ${pageNum}`) || 
                   doc.content?.includes(`page ${pageNum}`) ||
                   doc.pageNumber === parseInt(pageNum);
          });
          
          if (!docToUse && note.linkedDocuments && note.linkedDocuments.length > 0) {
            docToUse = note.linkedDocuments.find(doc => doc.fileUrl) || note.linkedDocuments[0];
          }
          
          if (!docToUse) {
            return (
              <span key={`${pIndex}-${index}`} dangerouslySetInnerHTML={{ __html: part.phrase }} />
            );
          }
          
          const uniqueKey = `${docToUse.id}-page${pageNum}`;
          const ref = documentReferences.get(uniqueKey);
          
          if (!ref) {
            return (
              <span key={`${pIndex}-${index}`} dangerouslySetInnerHTML={{ __html: part.phrase }} />
            );
          }
          
          const handleClick = (e) => {
            e.stopPropagation();
            
            if (docToUse.fileUrl) {
              let urlToOpen = docToUse.fileUrl.split('#')[0];
              window.open(urlToOpen, '_blank', 'noopener,noreferrer');
            }
          };
          
          const shortTitle = docToUse.title.length > 50 
            ? docToUse.title.substring(0, 47) + '...'
            : docToUse.title;
          
          return (
            <span key={`${pIndex}-${index}`} className="inline">
              <span
                onClick={handleClick}
                className="group relative cursor-pointer hover:bg-blue-50 px-1 -mx-1 rounded transition-colors inline"
              >
                <span 
                  className="underline decoration-blue-300 decoration-2 hover:decoration-blue-400 transition-colors"
                  dangerouslySetInnerHTML={{ __html: part.phrase }}
                />
                
                <sup className="ml-1">
                  <span
                    className="inline-flex items-center justify-center text-blue-600 bg-blue-50 border border-blue-200 hover:border-blue-400 font-semibold rounded-full shadow-sm hover:shadow transition-all"
                    style={{ 
                      fontSize: '0.7em', 
                      lineHeight: '1.2', 
                      width: '1.4em',
                      height: '1.4em',
                      padding: '0'
                    }}
                  >
                    {ref.num}
                  </span>
                </sup>
                
                <span className="invisible group-hover:visible absolute bottom-full left-0 transform mb-2 w-64 z-50 pointer-events-none">
                  <span className="block bg-white border-2 border-blue-100 rounded-lg shadow-xl p-3">
                    <span className="block text-sm font-semibold text-gray-800 mb-2">
                      {shortTitle}
                    </span>
                    <span className="block text-xs text-blue-500 font-medium mb-1">
                      📄 Page {pageNum}
                    </span>
                    <span className="block text-xs text-gray-500">
                      🖱️ Cliquez pour ouvrir le document
                    </span>
                  </span>
                  <span className="absolute top-full left-8 transform -mt-px">
                    <span className="block w-0 h-0 border-8 border-transparent border-t-blue-100"></span>
                    <span className="block w-0 h-0 border-6 border-transparent border-t-white absolute top-0 left-1/2 transform -translate-x-1/2 -mt-2"></span>
                  </span>
                </span>
              </span>
            </span>
          );
        }
        
        // Texte normal
        return (
          <span key={`${pIndex}-${index}`} dangerouslySetInnerHTML={{ __html: part.content }} />
        );
      });
      
      return (
        <p key={pIndex} className="mb-5 last:mb-0 text-justify leading-relaxed">
          {renderedParts}
        </p>
      );
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Recherche */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <h4 className="font-semibold text-blue-900">Recherche effectuée</h4>
        </div>
        <p className="text-gray-800 font-medium text-lg">
          {note.searchQuery || note.title}
        </p>
      </div>
      
      {/* Section Synthèse avec MathJax et références */}
      {note.synthesis && note.synthesis.summary && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-purple-500 rounded"></div>
            <h4 className="font-semibold text-purple-900">Synthèse des résultats</h4>
          </div>
          <MathJax>
            <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed">
              {renderTextWithReferences(note.synthesis.summary)}
            </div>
          </MathJax>
        </div>
      )}
      
      {/* Section Documents liés avec références citées intégrées */}
      {note.linkedDocuments && note.linkedDocuments.length > 0 && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-3">
            <File className="h-5 w-5 text-green-600" />
            <h4 className="font-semibold text-green-900">
              Documents sources ({note.linkedDocuments.length})
            </h4>
          </div>
          <div className="space-y-2">
            {note.linkedDocuments.map((doc, index) => {
              // Trouver toutes les références qui pointent vers ce document
              const docRefs = Array.from(documentReferences.entries())
                .filter(([key, ref]) => ref.doc.id === doc.id)
                .sort((a, b) => a[1].num - b[1].num);
              
              return (
                <div 
                  key={doc.id || index} 
                  className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-200 hover:border-green-400 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                      <File className="h-4 w-4 text-green-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {doc.title}
                      </p>
                      {doc.fileName && doc.fileName !== doc.title && (
                        <p className="text-xs text-gray-500 truncate">
                          {doc.fileName}
                        </p>
                      )}
                      {/* Afficher les références citées pour ce document */}
                      {docRefs.length > 0 && (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span className="text-xs text-gray-500">Références:</span>
                          {docRefs.map(([key, ref]) => (
                            <span
                              key={key}
                              className="inline-flex items-center justify-center text-blue-600 bg-blue-50 border border-blue-200 font-semibold rounded-full text-xs"
                              style={{ 
                                width: '1.3em',
                                height: '1.3em',
                                fontSize: '0.65em'
                              }}
                              title={`Page ${ref.page}`}
                            >
                              {ref.num}
                            </span>
                          ))}
                          <span className="text-xs text-gray-500">
                            ({docRefs.map(([k, r]) => `p.${r.page}`).join(', ')})
                          </span>
                        </div>
                      )}
                      {doc.score !== undefined && (
                        <p className="text-xs text-green-600 font-medium mt-0.5">
                          Pertinence: {Math.round(doc.score * 100)}%
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {doc.fileUrl && (
                    <a 
                      href={doc.fileUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="ml-3 flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex-shrink-0"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Ouvrir
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Métadonnées */}
      <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>
            Créé le {note.createdAt?.toDate?.().toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) || 'Date inconnue'}
          </span>
        </div>
        {note.updatedAt && note.updatedAt !== note.createdAt && (
          <span className="text-xs text-gray-400">
            Modifié le {note.updatedAt.toDate?.().toLocaleDateString('fr-FR')}
          </span>
        )}
      </div>
    </div>
  );
};

// Composant pour la liste vide
export const EmptyNotesList = () => {
  return (
    <div className="text-center py-12 px-4">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
        <FileText className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-700 mb-2">
        Aucune recherche sauvegardée
      </h3>
      <p className="text-sm text-gray-500 max-w-md mx-auto">
        Explorez vos documents et utilisez le bouton 
        <span className="font-semibold text-green-600"> "Sauvegarder cette recherche" </span>
        pour conserver vos découvertes importantes
      </p>
    </div>
  );
};